import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import VipTierCard, {
  TIER_DISPLAY,
  TIER_ORDER,
  type Tier,
} from '@/components/feature/VipTierCard';
import { useLanguage } from '@/localization';

interface StripePrice {
  priceId: string;
  months: number;
  amount: number;
  currency: string;
}

interface StripeTier {
  productId: string;
  name: string;
  features: string[];
  prices: StripePrice[];
}

type StripePrices = Partial<Record<Tier, StripeTier>> & {
  mode: 'test' | 'live' | 'disabled';
  checkoutEnabled: boolean;
};

function getBadge(months: number, allMonths: number[]): string | undefined {
  if (months === 3) return 'Popular';
  if (months === allMonths[allMonths.length - 1] && months > 1) return 'Best Value';
  return undefined;
}

function ManageSubscription() {
  const { t } = useLanguage();
  return (
    <div className="max-w-lg mx-auto mt-12">
      <div className="border border-gray-700 rounded-xl bg-gray-900/60 p-6 text-center">
        <i className="ri-settings-3-line text-3xl text-red-400 mb-3 block"></i>
        <h3 className="text-lg font-bold text-white mb-2 font-orbitron">{t('Manage Your Subscription')}</h3>
        <p className="text-gray-300 text-sm leading-relaxed">
          {t('After checkout, use the Manage Subscription button on the confirmation screen to update or cancel through Stripes secure portal.')}
        </p>
        <p className="text-gray-400 text-xs mt-3">
          {t('Need help? Contact us on')}{' '}
          <a href="https://discord.gg/vGX8GSrej6" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 underline">Discord</a>.
        </p>
      </div>
    </div>
  );
}

export default function VipPage() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prices, setPrices] = useState<StripePrices | null>(null);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState('');
  const [selectedTier, setSelectedTier] = useState<Tier>('vip');
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');
  const [steamId, setSteamId] = useState('');
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successType, setSuccessType] = useState<'vip' | 'donation'>('vip');
  const confettiFired = useRef(false);
  const sessionId = searchParams.get('session_id') || '';
  const paymentMode = prices?.mode;

  useEffect(() => {
    const isSuccess = searchParams.get('success') === 'true';
    const isDonation = searchParams.get('donation') === 'true';
    if (isSuccess || isDonation) {
      setSuccessType(isDonation ? 'donation' : 'vip');
      setShowSuccess(true);
      // Fire confetti
      if (!confettiFired.current) {
        confettiFired.current = true;
        const duration = 2000;
        const end = Date.now() + duration;
        const frame = () => {
          confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors: ['#dc2626', '#facc15', '#f97316'] });
          confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors: ['#dc2626', '#facc15', '#f97316'] });
          if (Date.now() < end) requestAnimationFrame(frame);
        };
        frame();
      }
    }
  }, []);

  const closeSuccess = () => {
    setShowSuccess(false);
    setSearchParams({}, { replace: true });
  };

  const manageSubscription = async () => {
    if (!sessionId) return;
    setPortalLoading(true);
    setError('');
    try {
      const res = await fetch('/map-api/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('Could not open subscription management'));
      if (!data.url) throw new Error(t('Stripe did not return a portal URL'));
      window.location.assign(data.url);
    } catch (portalError) {
      setError((portalError as Error).message);
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    fetch('/map-api/stripe/prices')
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setPricesError(data.error);
        } else {
          setPrices(data);
          // Check URL for preselected tier
          const params = new URLSearchParams(window.location.search);
          const urlTier = params.get('tier') as Tier | null;
          const initialTier = (urlTier && data[urlTier]) ? urlTier : (data.vip ? 'vip' : 'vip_plus');
          const tierData = data[initialTier];
          if (tierData?.prices?.length) {
            setSelectedTier(initialTier);
            setSelectedPriceId(tierData.prices[0].priceId);
          }
        }
        setPricesLoading(false);
      })
      .catch(() => {
        setPricesError(t('Failed to load pricing'));
        setPricesLoading(false);
      });
  }, []);

  const display = TIER_DISPLAY[selectedTier];
  const tierData = prices?.[selectedTier];
  const tierPrices = tierData?.prices || [];
  const selectedPlan = tierPrices.find(p => p.priceId === selectedPriceId);
  const monthlyBase = tierPrices.find(p => p.months === 1)?.amount || 0;

  const perMonth = selectedPlan ? selectedPlan.amount / selectedPlan.months : 0;
  const savePercent = monthlyBase > 0 && selectedPlan && selectedPlan.months > 1
    ? Math.round((1 - perMonth / monthlyBase) * 100)
    : 0;

  const handleTierChange = (tier: Tier) => {
    setSelectedTier(tier);
    const firstPrice = prices?.[tier]?.prices?.[0];
    if (firstPrice) setSelectedPriceId(firstPrice.priceId);
    setError('');
  };

  const handleCheckout = async () => {
    const trimmed = steamId.trim();
    if (!trimmed) {
      setError(t('Please enter your Steam ID'));
      return;
    }
    if (!/^7656119\d{10}$/.test(trimmed)) {
      setError(t('Please enter a valid Steam64 ID (17 digits starting with 7656119...)'));
      return;
    }
    if (!selectedPriceId) {
      setError(t('Please select a plan'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await fetch('/map-api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: selectedPriceId,
          steamId: trimmed,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || t('Failed to create checkout session'));
      }
    } catch {
      setError(t('Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navigation />
      <main className="relative z-10 pt-20 pb-16 flex-1">
        <div className="container mx-auto px-4">
          <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm mb-8 inline-block">
            <i className="ri-arrow-left-s-line"></i> {t('Back to Home')}
          </Link>

          {paymentMode !== 'live' && (
          <div className="mb-8 rounded-xl border border-amber-500/50 bg-amber-950/30 p-4">
            <div className="flex items-start gap-3">
              <i className="ri-test-tube-line mt-0.5 text-xl text-amber-300"></i>
              <div>
                <p className="font-orbitron font-bold uppercase tracking-wider text-amber-200">
                  {paymentMode === 'test' ? t('Stripe test mode') : t('Checking Stripe mode')}
                </p>
                <p className="mt-1 text-sm text-amber-100/75">
                  {paymentMode === 'test'
                      ? t('No real charge will be made. Checkout is restricted to the approved test account.')
                      : t('Checkout remains disabled until Stripe configuration is confirmed.')}
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold font-orbitron horror-heading mb-4">
              {t('Become a VIP Member')}
            </h1>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('Support our server and unlock exclusive perks, rewards, and recognition')}
            </p>
            <p className="text-sm text-gray-500 mt-3">
              {t('All plans are recurring subscriptions — you can cancel anytime')}
            </p>
          </div>

          {pricesLoading ? (
            <div className="py-20 text-center">
              <i className="ri-loader-4-line text-3xl text-red-500 animate-spin block mb-3"></i>
              <p className="text-gray-400">{t('Loading plans...')}</p>
            </div>
          ) : pricesError ? (
            <div className="py-20 text-center">
              <i className="ri-error-warning-line text-3xl text-red-500 block mb-3"></i>
              <p className="text-gray-400">{t('Pricing unavailable right now. Please try again later.')}</p>
            </div>
          ) : (
            <>
              {/* Tier Selection */}
              <div className="grid md:grid-cols-2 gap-8 lg:gap-10 max-w-4xl mx-auto mb-12 items-stretch">
                {TIER_ORDER.map((key) => {
                  const data = prices?.[key];
                  if (!data) return null;
                  const isSelected = selectedTier === key;
                  const monthlyPrice =
                    data.prices.find((p) => p.months === 1)?.amount ?? null;

                  return (
                    <VipTierCard
                      key={key}
                      tier={key}
                      name={data.name}
                      monthlyPrice={monthlyPrice}
                      selected={isSelected}
                      onClick={() => handleTierChange(key)}
                    />
                  );
                })}
              </div>

              {/* Duration Selection */}
              {tierPrices.length > 0 && (
                <div className="max-w-4xl mx-auto mb-12">
                  <h3 className="text-2xl font-bold text-white mb-6 font-orbitron text-center">
                    {t('Choose Your Plan')}
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {tierPrices.map((plan) => {
                      const isSelected = selectedPriceId === plan.priceId;
                      const pm = plan.amount / plan.months;
                      const save = monthlyBase > 0 && plan.months > 1
                        ? Math.round((1 - pm / monthlyBase) * 100)
                        : 0;
                      const allMonths = tierPrices.map(p => p.months);
                      const badge = getBadge(plan.months, allMonths);

                      return (
                        <button
                          key={plan.priceId}
                          onClick={() => { setSelectedPriceId(plan.priceId); setError(''); }}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-center cursor-pointer ${
                            isSelected
                              ? 'border-red-500 bg-red-950/30 shadow-lg shadow-red-500/10'
                              : 'border-gray-700 hover:border-gray-500 bg-black/40'
                          }`}
                        >
                          {badge && (
                            <div className={`absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider whitespace-nowrap ${
                              badge === 'Best Value' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                            }`}>
                              {t(badge.toUpperCase())}
                            </div>
                          )}

                          <div className="font-bold text-white text-sm mb-1">
                            {plan.months} {plan.months > 1 ? t('Months') : t('Month')}
                          </div>
                          <div className={`text-xl font-bold ${display.textColor} font-orbitron`}>
                            {'\u20AC'}{plan.amount.toFixed(2)}
                          </div>
                          {plan.months > 1 && (
                            <>
                              <div className="text-xs text-gray-400 mt-1">{'\u20AC'}{pm.toFixed(2)}/mo</div>
                              {save > 0 && (
                                <div className="text-xs text-green-400 font-bold mt-0.5">{t('Save')} {save}%</div>
                              )}
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Steam ID + Checkout */}
              {selectedPlan && (
                <div className="max-w-lg mx-auto">
                  <Card variant="blood" className="p-6">
                    <h3 className="text-lg font-bold text-white mb-4 font-orbitron text-center">
                      {t('Complete Your Purchase')}
                    </h3>

                    {/* Order Summary */}
                    <div className="bg-black/40 rounded-lg p-4 mb-5 border border-red-900/20">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400">{t('Plan')}</span>
                        <span className="text-white font-bold">
                          {tierData?.name} &mdash; {selectedPlan.months} {selectedPlan.months > 1 ? t('Months') : t('Month')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400">{t('Billed every')}</span>
                        <span className="text-white">
                          {selectedPlan.months === 1 ? t('month') : `${selectedPlan.months} ${t('months')}`}
                        </span>
                      </div>
                      <div className="border-t border-gray-700 my-2"></div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{t('Total')}</span>
                        <span className={`text-2xl font-bold ${display.textColor} font-orbitron`}>
                          {'\u20AC'}{selectedPlan.amount.toFixed(2)}
                        </span>
                      </div>
                      {selectedPlan.months > 1 && (
                        <div className="text-right text-xs text-gray-400 mt-1">
                          ({'\u20AC'}{perMonth.toFixed(2)}{t('/month')}
                          {savePercent > 0 && <span className="text-green-400 ml-1">— {t('Save')} {savePercent}%</span>})
                        </div>
                      )}
                    </div>

                    {/* Steam ID Input */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('Your Steam ID')}
                      </label>
                      <input
                        type="text"
                        value={steamId}
                        onChange={(e) => { setSteamId(e.target.value); setError(''); }}
                        placeholder="76561198012345678"
                        className="w-full px-4 py-3 bg-black/60 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        {t('Find your Steam64 ID at')}{' '}
                        <a href="https://steamid.io" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                          steamid.io
                        </a>
                      </p>
                    </div>

                    {error && (
                      <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-lg text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={handleCheckout}
                      disabled={loading || !prices?.checkoutEnabled}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          {t('Redirecting to checkout...')}
                        </span>
                      ) : (
                        `${paymentMode === 'test' ? t('Test Subscription') : t('Subscribe Now')} \u2014 \u20AC${selectedPlan.amount.toFixed(2)}`
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-2 mt-3 text-gray-500 text-xs">
                      <i className="ri-lock-line"></i>
                      {t('Secure payment powered by Stripe')}
                    </div>
                    <p className="mt-2 text-center text-xs text-gray-500">
                      {t('By subscribing, you agree to the')}{' '}
                      <a href="https://uliunai.com/terms" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                        {t('Terms of Service')}
                      </a>{' '}
                      {t('and')}{' '}
                      <a href="https://uliunai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                        {t('Privacy Policy')}
                      </a>.
                    </p>
                  </Card>
                </div>
              )}

              {/* Manage Subscription */}
              <ManageSubscription />

              {/* Trust badges */}
              <div className="max-w-4xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <i className="ri-shield-check-line text-3xl text-green-400 mb-2 block"></i>
                  <h4 className="text-white font-bold mb-1">{t('Secure Payments')}</h4>
                  <p className="text-gray-400 text-sm">{t('All payments processed securely through Stripe')}</p>
                </div>
                <div className="text-center">
                  <i className="ri-flashlight-line text-3xl text-yellow-400 mb-2 block"></i>
                  <h4 className="text-white font-bold mb-1">{t('Fast Activation')}</h4>
                  <p className="text-gray-400 text-sm">{t('Your VIP perks activate on the next map change after payment')}</p>
                </div>
                <div className="text-center">
                  <i className="ri-refresh-line text-3xl text-blue-400 mb-2 block"></i>
                  <h4 className="text-white font-bold mb-1">{t('Cancel Anytime')}</h4>
                  <p className="text-gray-400 text-sm">{t('No commitment — cancel your subscription whenever you want')}</p>
                </div>
              </div>

            </>
          )}
        </div>
      </main>
      <Footer />

      {/* Success modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={closeSuccess}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 ${successType === 'donation' ? 'bg-red-600/20 border-2 border-red-500' : 'bg-green-600/20 border-2 border-green-500'}`}>
              <i className={`text-3xl ${successType === 'donation' ? 'ri-heart-fill text-red-400' : 'ri-check-double-line text-green-400'}`}></i>
            </div>

            <h2 className="text-2xl font-bold font-orbitron text-white mb-3">
              {successType === 'donation' ? (
                <>{t('Thank You!')}</>
              ) : (
                <>{t('Welcome to VIP!')}</>
              )}
            </h2>

            <p className="text-gray-300 mb-4">
              {successType === 'donation'
                ? t('Your donation means a lot! Every contribution helps keep the Uliunai server running.')
                : t('Your VIP perks will activate on the next map change. Rejoin the server after a map change to see your new perks!')}
            </p>

            {successType !== 'donation' && (
              <p className="text-gray-400 text-sm mb-5">
                {t('Manage or cancel securely through Stripe using the button below.')}
              </p>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-center">
              {successType !== 'donation' && sessionId && (
                <button
                  type="button"
                  onClick={manageSubscription}
                  disabled={portalLoading}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition-colors text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {portalLoading ? t('Opening Stripe...') : (paymentMode === 'test' ? t('Manage Test Subscription') : t('Manage Subscription'))}
                </button>
              )}
              <Link to="/" className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition-colors text-sm">
                Back to Home
              </Link>
              <button onClick={closeSuccess} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg font-bold transition-colors text-sm cursor-pointer">
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
